import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://localhost:7271/api';

// סוג הצד (תואם enum LeadSourceType בשרת): 1=סוכן, 2=ארגון
export enum PartyType {
  Agent = 1,
  Organization = 2,
}

export const PartyTypeOptions: { value: PartyType; label: string }[] = [
  { value: PartyType.Agent, label: 'סוכן' },
  { value: PartyType.Organization, label: 'ארגון' },
];

// ----- Types -----

/** ליד שהופנה דרך הצד וטרם חתם — לצפי בלבד, ללא סכומי חוב. */
export interface PendingLeadRow {
  leadId: number;
  leadName: string;
  statusName: string;
  statusColor?: string | null;
  createdAt: string;
}

export interface AgentBalanceRow {
  partyType: number;
  partyTypeName: string;
  sourceId: number;
  sourceName: string;
  signedClientCount: number;
  approvedClientCount: number;
  pendingLeadCount: number;
  totalOwed: number;
  totalPaid: number;
  remaining: number;
  isSettled: boolean;
}

export interface AgentBalanceSummary {
  items: AgentBalanceRow[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
  grandTotalOwed: number;
  grandTotalPaid: number;
  grandTotalRemaining: number;
  openPartiesCount: number;
}

export interface AgentPayment {
  id: number;
  clientId: number;
  clientName: string;
  partySourceId: number;
  partySourceName: string;
  partyType: number;
  partyTypeName: string;
  amount: number;
  paymentDate: string;
  note?: string | null;
  reference?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ClientBalanceRow {
  clientId: number;
  clientName: string;
  isAllowanceApproved: boolean;
  signingOwed: number;
  approvalOwed?: number | null; // null = אינו קיים (לא בשל) — לא מוצג
  totalOwed: number;
  totalPaid: number;
  remaining: number;
  payments: AgentPayment[];
}

export interface PartyBalanceDetail {
  partyType: number;
  partyTypeName: string;
  sourceId: number;
  sourceName: string;
  signedClientCount: number;
  approvedClientCount: number;
  pendingLeadCount: number;
  totalOwed: number;
  totalPaid: number;
  remaining: number;
  clients: ClientBalanceRow[];
  pendingLeads: PendingLeadRow[];
}

export interface ClientOption {
  id: number;
  name: string;
}

export interface AgentBalanceFilter {
  page?: number;
  pageSize?: number;
  partyType?: number | null;
  searchTerm?: string;
  clientId?: number | null;
  openOnly?: boolean;
}

export interface CreateAgentPayment {
  clientId: number;
  partySourceId: number;
  partyType: number;
  amount: number;
  paymentDate: string;
  note?: string | null;
  reference?: string | null;
}

export interface UpdateAgentPayment {
  amount: number;
  paymentDate: string;
  note?: string | null;
  reference?: string | null;
}

export interface BulkCreateAgentPaymentItem {
  clientId: number;
  amount: number;
}

export interface BulkCreateAgentPayment {
  partySourceId: number;
  partyType: number;
  paymentDate: string;
  note?: string | null;
  reference?: string | null;
  items: BulkCreateAgentPaymentItem[];
}

export interface BulkCreateAgentPaymentResult {
  count: number;
  total: number;
  payments: AgentPayment[];
}

const BASE = `${API_BASE_URL}/balances`;

export const balancesApi = {
  getSummary: async (filter: AgentBalanceFilter): Promise<AgentBalanceSummary> => {
    const response = await axios.get<AgentBalanceSummary>(BASE, { params: filter });
    return response.data;
  },

  getPartyDetail: async (partyType: number, sourceId: number): Promise<PartyBalanceDetail> => {
    const response = await axios.get<PartyBalanceDetail>(`${BASE}/party`, {
      params: { type: partyType, sourceId },
    });
    return response.data;
  },

  getAssignedClients: async (): Promise<ClientOption[]> => {
    const response = await axios.get<ClientOption[]>(`${BASE}/clients`);
    return response.data;
  },

  createPayment: async (dto: CreateAgentPayment): Promise<AgentPayment> => {
    const response = await axios.post<AgentPayment>(`${BASE}/payments`, dto);
    return response.data;
  },

  createPaymentsBulk: async (dto: BulkCreateAgentPayment): Promise<BulkCreateAgentPaymentResult> => {
    const response = await axios.post<BulkCreateAgentPaymentResult>(`${BASE}/payments/bulk`, dto);
    return response.data;
  },

  updatePayment: async (id: number, dto: UpdateAgentPayment): Promise<AgentPayment> => {
    const response = await axios.put<AgentPayment>(`${BASE}/payments/${id}`, dto);
    return response.data;
  },

  deletePayment: async (id: number): Promise<void> => {
    await axios.delete(`${BASE}/payments/${id}`);
  },
};

// ----- Helpers (פורמט he-IL, תואם שאר השירותים) -----

export const formatCurrency = (amount: number | undefined | null): string => {
  if (amount === undefined || amount === null) return '-';
  return `₪${amount.toLocaleString('he-IL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

export const formatDate = (dateString?: string | null): string => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleDateString('he-IL', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

/** תאריך היום בפורמט yyyy-MM-dd עבור input[type=date] (ברירת-מחדל לתשלום). */
export const todayIso = (): string => {
  const d = new Date();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}`;
};
